import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Format filename for Tesla Lock Sound downloads
 * Appends "TLs" suffix to maintain brand consistency
 * @param title - The sound title
 * @param format - The file format (e.g., "WAV")
 * @returns Formatted filename with TLs suffix
 */
export function formatDownloadFilename(title: string, format: string): string {
  // Remove special characters and replace spaces with underscores
  const cleanTitle = title
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '_')
    .toLowerCase()

  return `${cleanTitle}.${format.toLowerCase()}`
}

// Strip trailing "(flavor text)" from sound titles for display.
// The parenthetical is always SEO redundancy with the description field;
// keep the raw title for analytics/filenames, show the clean one in the UI.
export function cleanSoundTitle(title: string): string {
  const stripped = title.replace(/\s*\([^()]*\)\s*$/, '').trim();
  return stripped.length >= 3 ? stripped : title;
}

// Audio utility functions
export const audioUtils = {
  // Preload audio file to check accessibility
  async preloadAudio(url: string): Promise<{ success: boolean; error?: string; duration?: number }> {
    return new Promise((resolve) => {
      const audio = new Audio()
      let resolved = false
      
      const cleanup = () => {
        if (!resolved) {
          resolved = true
          audio.removeEventListener('canplaythrough', onCanPlayThrough)
          audio.removeEventListener('error', onError)
          audio.removeEventListener('loadedmetadata', onLoadedMetadata)
        }
      }
      
      const onCanPlayThrough = () => {
        cleanup()
        resolve({ 
          success: true, 
          duration: audio.duration || 0 
        })
      }
      
      const onLoadedMetadata = () => {
        cleanup()
        resolve({ 
          success: true, 
          duration: audio.duration || 0 
        })
      }
      
      const onError = () => {
        cleanup()
        const errorMessage = audio.error 
          ? `Audio error: ${audio.error.code}` 
          : 'Unknown audio error'
        resolve({ 
          success: false, 
          error: errorMessage 
        })
      }
      
      // Set timeout to prevent hanging
      setTimeout(() => {
        if (!resolved) {
          cleanup()
          resolve({ 
            success: false, 
            error: 'Audio preload timeout' 
          })
        }
      }, 10000)
      
      audio.addEventListener('canplaythrough', onCanPlayThrough)
      audio.addEventListener('loadedmetadata', onLoadedMetadata)
      audio.addEventListener('error', onError)
      
      audio.preload = 'metadata'
      audio.src = url
    })
  },
  
  // Check if browser supports audio format
  canPlayFormat(format: string): boolean {
    const audio = new Audio()
    const mimeType = format.toLowerCase() === 'wav' 
      ? 'audio/wav' 
      : `audio/${format.toLowerCase()}`
    
    const support = audio.canPlayType(mimeType)
    return support === 'probably' || support === 'maybe'
  },
  
  // Get user-friendly error message
  getErrorMessage(error: MediaError | null): string {
    if (!error) return 'Unknown audio error'
    
    // Handle cases where MediaError is not available (e.g., in Node.js test environment)
    const errorCode = typeof error.code === 'number' ? error.code : 0
    
    switch (errorCode) {
      case 1: // MEDIA_ERR_ABORTED
        return 'Audio loading was stopped'
      case 2: // MEDIA_ERR_NETWORK
        return 'Network error while loading audio'
      case 3: // MEDIA_ERR_DECODE
        return 'Audio file is corrupted or unsupported'
      case 4: // MEDIA_ERR_SRC_NOT_SUPPORTED
        return 'Audio format not supported'
      default:
        return error.message || 'Unknown audio error'
    }
  }
}
